import { ListSourceType } from '../../../models/ListSourceType';
import { BulkAddProductsToSelectionListResponse } from '../../store/api';
import { SOURCE_SELECTIONLIST } from '../../store/constants';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { invalidateCartSummaryQuery } from '../shopping-cart/useCartSummaryQuery';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { invalidateSelectionListSummary } from './useSelectionListSummary';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateShoppingCartProducts } from '../shopping-cart/useShoppingCartProducts';
import {
    moveBundleGroupFromCartToListByBundleId,
    MoveGroupFromShoppingCartByBundleIdRequest
} from './moveBundleGroupFromCartToListByBundleId';
import {
    moveBundleGroupFromListToListByBundleId,
    MoveBundleGroupFromSelectionListByBundleIdRequest
} from './moveBundleGroupFromListToListByBundleId';
import { invalidateSelectionListGroupedItems } from './useSelectionListGroupedItems';
import { invalidateCartGroupedItems } from '../shopping-cart/useCartGroupedItems';
import { bulkUpdateToList } from './bulkUpdateToList';

type GetRequestParams = (
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      }
) & {
    selectionListId: string;
    bundleId: string;
    performChecks: boolean;
};

export const getMoveProductsRequest = (
    { selectionListId, bundleId, performChecks, ...sourceParams }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        try {
            const request: MoveGroupFromShoppingCartByBundleIdRequest = { bundleId };
            const params = { performChecks };

            if (sourceParams.source === SOURCE_SELECTIONLIST) {
                const requestWithSourceList: MoveBundleGroupFromSelectionListByBundleIdRequest = {
                    ...request,
                    sourceSelectionListId: sourceParams.sourceSelectionListId
                };
                return moveBundleGroupFromListToListByBundleId({
                    selectionListId,
                    body: requestWithSourceList,
                    params
                });
            }

            return moveBundleGroupFromCartToListByBundleId({ selectionListId, body: request, params });
        } catch (e) {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props =
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      };

export const useMoveBundleGroupToSelectionList = (props: Props) => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        moveToListBulk: (bundleId: string, listId: string, performChecks: boolean) => {
            const apiCallback = getMoveProductsRequest(
                {
                    selectionListId: listId,
                    bundleId,
                    performChecks,
                    ...props
                },
                t
            );

            return bulkUpdateToList({
                performChecks,
                dispatch: dispatchRedux,
                setLoading,
                source: props.source,
                notificationMessage:
                    props.source === ListSourceType.SELECTION_LIST
                        ? t('notifications.moveProductsToList.message')
                        : t('notifications.moveCartToList.message'),
                apiCallback,
                t,
                callback: () => {
                    if (props.source === ListSourceType.SELECTION_LIST) {
                        invalidateSelectionListProducts(props.sourceSelectionListId).then();
                        invalidateSelectionListAggregations(props.sourceSelectionListId).then();
                        invalidateSelectionListSummary(props.sourceSelectionListId).then();
                        invalidateSelectionListGroupedItems(props.sourceSelectionListId).then();
                    } else {
                        invalidateCartAggregations().then();
                        invalidateCartSummaryQuery().then();
                        invalidateCartGroupedItems().then();
                        invalidateShoppingCartProducts().then();
                    }
                }
            });
        }
    };
};
